import { DestroyRef, inject, Service } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { NavigationEnd, Router } from '@angular/router';
import { AuthenticationService } from '@app/core/authentication/authentication.service';
import { firstValueFrom } from 'rxjs';
import { filter } from 'rxjs/operators';

import { ActivationLinkService } from '../activation-link.service';
import { TenantLifecycleService } from '../tenant-lifecycle.service';
import { ActivationCheckout, ActivationConfirmResult, canActivate } from './activation-checkout';
import { ActivationCheckoutError, ActivationCheckoutService } from './activation-checkout.service';
import { ActivationDialogComponent, ActivationDialogData } from './activation-dialog.component';
import {
  ACTIVATION_URL_PARAMS,
  ActivationDeepLink,
  ActivationRedirect,
  consumeRememberedActivationDeepLink,
  readActivationDeepLink,
} from './activation-redirect';

/**
 * Entry point of "Plan aktivieren" (sidebar card, read-only notice, ?activate=1).
 *
 * Company Administrators get the in-app modal when the tenant API offers a
 * checkout; a contact mode opens the meeting link and everything else (502,
 * network, unknown answers) falls back to the activation link flow. Everyone
 * else keeps the link flow, which tells them that only Company Admins can
 * activate the plan.
 */
@Service()
export class ActivationFlowService {
  private readonly dialog = inject(MatDialog);
  private readonly router = inject(Router);
  private readonly auth = inject(AuthenticationService);
  private readonly checkout = inject(ActivationCheckoutService);
  private readonly link = inject(ActivationLinkService);
  private readonly lifecycle = inject(TenantLifecycleService);

  private dialogRef: MatDialogRef<ActivationDialogComponent, ActivationConfirmResult | null> | null = null;
  private prefetchStarted = false;
  private handledDeepLink: string | null = null;

  /** Label of the activation button: "Plan aktivieren" or "Termin vereinbaren". */
  readonly labelKey = this.link.labelKey;
  readonly kind = this.link.kind;

  /**
   * Asks once in the background what a click will do, so the label is right
   * and the click can open the contact link without a popup blocker.
   * Admins never fetch a checkout link they will not use.
   */
  prefetch(): void {
    if (this.prefetchStarted) {
      return;
    }
    this.prefetchStarted = true;
    if (!this.isCompanyAdministrator()) {
      this.link.prefetch();
      return;
    }
    void this.checkout.prefetch().then((checkout) => {
      if (checkout === null || checkout.mode === 'unavailable') {
        this.link.prefetch();
      } else {
        this.link.rememberKind(checkout.mode === 'contact' ? 'contact' : 'activate');
      }
    });
  }

  /** Click on "Plan aktivieren". Must run in the click handler itself (new tabs). */
  start(): void {
    if (!this.isCompanyAdministrator()) {
      void this.link.open();
      return;
    }
    this.route(this.checkout.peek());
  }

  /**
   * Opens the modal for ?activate=1 (also when the login swallowed it, see
   * rememberActivationDeepLink) and for the way back from a Stripe redirect,
   * on every navigation.
   */
  watchDeepLinks(destroyRef: DestroyRef): void {
    this.handleDeepLink();
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(destroyRef),
      )
      .subscribe(() => this.handleDeepLink());
  }

  handleDeepLink(): void {
    const fromAddress = readActivationDeepLink(this.router.routerState.snapshot.root.queryParamMap);
    const remembered = consumeRememberedActivationDeepLink();
    const deepLink: ActivationDeepLink | null = fromAddress ?? (remembered ? { kind: 'open' } : null);
    const signature = fromAddress === null ? null : JSON.stringify(fromAddress);
    // The first navigation can report the same link twice before the parameters are gone.
    if (deepLink === null || (fromAddress !== null && signature === this.handledDeepLink)) {
      this.handledDeepLink = signature;
      return;
    }
    this.handledDeepLink = signature;
    if (fromAddress !== null) {
      this.removeDeepLinkParams();
    }

    if (!this.isCompanyAdministrator()) {
      this.link.notify('forbidden');
      return;
    }
    if (deepLink.kind === 'redirect') {
      this.openDialog(null, deepLink.redirect);
      return;
    }
    void this.openFromLink();
  }

  /**
   * ?activate=1: the modal opens by itself only when there is something to
   * activate (a subscription that is not active yet). A paying workspace has
   * nothing to activate; a stale mail link just lands on the page. Without
   * an in-app checkout the workspace keeps the earlier way (contact or the
   * activation link page), as long as it is not active.
   */
  private async openFromLink(): Promise<void> {
    let checkout: ActivationCheckout | null = this.checkout.peek();
    if (checkout === null) {
      try {
        checkout = await this.checkout.load();
      } catch (error: unknown) {
        if (error instanceof ActivationCheckoutError && error.failure === 'forbidden') {
          this.link.notify('forbidden');
          return;
        }
        checkout = null;
      }
    }
    if (checkout?.mode === 'checkout') {
      if (canActivate(checkout)) {
        this.openDialog(checkout);
      }
      return;
    }
    if (this.lifecycle.state() !== 'Active') {
      this.route(checkout);
    }
  }

  private route(checkout: ActivationCheckout | null): void {
    if (checkout?.mode === 'contact' && checkout.contactUrl !== null) {
      this.link.openUrl(checkout.contactUrl);
      return;
    }
    if (checkout?.mode === 'unavailable') {
      void this.link.open();
      return;
    }
    this.openDialog(checkout);
  }

  private openDialog(checkout: ActivationCheckout | null, redirect: ActivationRedirect | null = null): void {
    if (this.dialogRef !== null) {
      return;
    }
    const data: ActivationDialogData = { checkout, redirect };
    const ref = this.dialog.open<ActivationDialogComponent, ActivationDialogData, ActivationConfirmResult | null>(
      ActivationDialogComponent,
      {
        data,
        width: '560px',
        maxWidth: 'calc(100vw - 32px)',
        maxHeight: 'calc(100dvh - 32px)',
        ariaLabelledBy: 'activation-dialog-title',
        autoFocus: 'dialog',
        restoreFocus: true,
        panelClass: 'hc-activation-dialog',
      },
    );
    this.dialogRef = ref;
    void firstValueFrom(ref.afterClosed(), { defaultValue: null }).then(() => {
      this.dialogRef = null;
    });
  }

  private removeDeepLinkParams(): void {
    const queryParams = Object.fromEntries(ACTIVATION_URL_PARAMS.map((param) => [param, null]));
    void this.router.navigate([], { queryParams, queryParamsHandling: 'merge', replaceUrl: true });
  }

  private isCompanyAdministrator(): boolean {
    return this.auth.isCompanyAdministrator();
  }
}
